import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import Modal from '../shared/Modal';

// ─── Sage ────────────────────────────────────────────────────────────────────
//
// Connection, reference data and supplier mapping for Sage Accounting.
//
// Sage is the accounting system of record, so this screen is built to make the
// state obvious rather than to be quick: whether posting is on, whether the
// reference data actually arrived, and which suppliers still cannot be posted
// against. Nothing here posts anything. Posting happens on a specific invoice,
// pressed by a person, from the Accounts Inbox.
//
// The ledger account is deliberately NOT mapped from the overhead category any
// more. Categories are a management-reporting taxonomy and the nominal ledger is
// an accounting structure; "Vehicles" spans fuel, servicing and vehicle
// insurance across separate codes. The category now suggests a code and the
// person coding the invoice picks the right one.

const T = {
  label: 'text-[9px] font-semibold uppercase tracking-wide',
  meta: 'text-[10px]',
  body: 'text-[11px]',
  strong: 'text-[12px] font-semibold',
  title: 'text-[13px] font-bold',
  page: 'text-base font-extrabold',
};

const sel = 'border border-gray-200 rounded px-2 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-navy';
const btn = 'px-3 py-1.5 rounded-md border text-[11px] font-semibold transition-colors disabled:opacity-50';
const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Searchable picker ───────────────────────────────────────────────────────
// A dropdown of 139 Sage contacts is not usable. This is the same idea as the
// ledger picker in the inbox: type, see matches, choose.
function SearchPicker({ options, value, onChange, placeholder, className }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const wrapRef = useRef();

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = options.find(o => o.value === value);
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;

  return (
    <div ref={wrapRef} className="relative">
      <input
        className={className}
        value={open ? query : (selected?.label || '')}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        placeholder={placeholder || 'Search…'}
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto text-[11px]">
          <button className="w-full text-left px-2 py-1 text-gray-400 hover:bg-gray-50 border-b border-gray-100"
            onClick={() => { onChange(''); setOpen(false); }}>— not mapped —</button>
          {filtered.slice(0, 200).map(o => (
            <button key={o.value}
              className={`w-full text-left px-2 py-1 hover:bg-blue-50 ${o.value === value ? 'bg-blue-50 font-semibold text-navy' : 'text-gray-700'}`}
              onClick={() => { onChange(o.value); setOpen(false); }}>{o.label}</button>
          ))}
          {!filtered.length && <div className="px-2 py-1 text-gray-400 italic">No match</div>}
        </div>
      )}
    </div>
  );
}

function StatusPanel({ status, onConnect, onSync, onDisconnect, onDiagnose, busy }) {
  if (!status) return null;

  if (!status.credentials_configured) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
        <div className={`${T.strong} text-amber-900`}>Sage credentials are not configured</div>
        <p className={`${T.meta} text-amber-800 mt-1`}>
          The client ID and secret from the Sage developer portal need setting as Worker secrets before
          anything can connect. They are deliberately not stored in the database:
        </p>
        <pre className="mt-2 bg-white border border-amber-200 rounded p-2 text-[10px] overflow-x-auto">
{`wrangler secret put SAGE_CLIENT_ID
wrangler secret put SAGE_CLIENT_SECRET`}
        </pre>
      </div>
    );
  }

  const counts = status.counts || {};
  const nothingSynced = status.connected && !counts.ledger_accounts && !counts.tax_rates && !counts.contacts;

  // A rejected authorisation is not a working connection, however much of one is
  // still stored: nothing can be posted, checked or synced until somebody
  // reconnects. It gets the panel's whole colour so it cannot be read as a
  // healthy connection with a note attached.
  const needsReauth = !!status.reauth_required;

  return (
    <div className={`rounded-lg border p-3 ${needsReauth ? 'border-red-300 bg-red-50' : status.connected ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className={`${T.strong} ${needsReauth ? 'text-red-900' : status.connected ? 'text-emerald-900' : 'text-gray-800'}`}>
            {needsReauth
              ? 'Sage has disconnected — reconnect to post anything'
              : status.connected ? `Connected${status.business_name ? ` to ${status.business_name}` : ''}` : 'Not connected to Sage'}
          </div>
          <div className={`${T.meta} ${needsReauth ? 'text-red-800' : status.connected ? 'text-emerald-800' : 'text-gray-500'} mt-0.5`}>
            {needsReauth
              ? `Sage rejected the stored authorisation${status.reauth_required_at ? ` on ${status.reauth_required_at}` : ''}. `
                + 'Until it is reconnected, no invoice can be posted or checked for duplicates, and reference data cannot be refreshed. '
                + 'Press Reconnect — nothing already in Sage is affected.'
              : status.connected
                ? `Reference data last synced ${status.last_sync_at || 'never'}`
                  // The connection is checked every six hours, which both proves it
                  // works and keeps the token alive — Sage expires one that goes a
                  // month unused. Shown because "connected" is otherwise only a
                  // statement about what is stored, not about whether it works.
                  + (status.last_health_check_at ? ` · connection last verified ${status.last_health_check_at}` : '')
                : 'Connecting is read-only. Posting stays off until it is turned on separately.'}
          </div>
          {status.last_error && !needsReauth && (
            <div className={`${T.meta} text-red-700 mt-1`}>{status.last_error}</div>
          )}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {(!status.connected || needsReauth) && (
            <button onClick={onConnect} disabled={busy} className={`${btn} bg-navy text-white border-navy hover:bg-navy/90`}>
              {needsReauth ? 'Reconnect to Sage' : 'Connect to Sage'}
            </button>
          )}
          {status.connected && (
            <>
              <button onClick={onSync} disabled={busy} className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}>
                {busy ? 'Syncing…' : 'Refresh from Sage'}
              </button>
              <button onClick={onDiagnose} disabled={busy} className={`${btn} border-gray-300 text-gray-600 bg-white hover:bg-gray-50`}>
                Diagnose
              </button>
              <button onClick={onDisconnect} disabled={busy} className={`${btn} border-red-300 text-red-700 bg-white hover:bg-red-50`}>
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>

      {status.connected && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          {[
            { label: 'Ledger accounts', value: counts.ledger_accounts ?? 0, warn: !counts.ledger_accounts },
            // A tax rate with no percentage against it matches nothing, so it
            // blocks every VAT-bearing invoice. Showing the row count alone
            // read "5 tax rates" and looked fine while nothing could post.
            {
              label: 'Tax rates usable',
              value: `${counts.tax_rates_usable ?? 0}/${counts.tax_rates ?? 0}`,
              warn: !counts.tax_rates || (counts.tax_rates_usable ?? 0) < (counts.tax_rates ?? 0),
            },
            { label: 'Suppliers in Sage', value: counts.contacts ?? 0, warn: !counts.contacts },
            {
              label: 'Invoiced suppliers mapped',
              value: `${status.suppliers_invoiced_mapped ?? 0}/${status.suppliers_invoiced ?? 0}`,
              warn: (status.suppliers_invoiced ?? 0) > (status.suppliers_invoiced_mapped ?? 0),
            },
          ].map(c => (
            <div key={c.label} className="bg-white rounded border border-gray-200 px-2 py-1.5">
              <div className={`${T.label} text-gray-500`}>{c.label}</div>
              <div className={`text-[16px] font-bold tabular-nums ${c.warn ? 'text-amber-700' : 'text-navy'}`}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Zero of everything after a sync is a specific, diagnosable failure, not
          a shrug. The request shapes were written against the published v3.1
          contract without ever reaching the live API, so this is the expected
          first problem and it says exactly what to press. */}
      {nothingSynced && (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-2">
          <div className={`${T.strong} text-amber-900`}>Connected, but nothing came back</div>
          <p className={`${T.meta} text-amber-800 mt-1`}>
            Press <strong>Refresh from Sage</strong>, and if the counts stay at zero press <strong>Diagnose</strong>.
            That shows exactly what Sage returned for each endpoint, which is what identifies whether this is a
            permissions problem or a field-name mismatch in the sync.
          </p>
        </div>
      )}

      {/* Rates arrived but with no percentages. This blocks every VAT-bearing
          invoice, and it did so silently: the count said 5 and looked fine. */}
      {status.connected && counts.tax_rates > 0 && (counts.tax_rates_usable ?? 0) === 0 && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 p-2">
          <div className={`${T.strong} text-red-900`}>
            {counts.tax_rates} tax rates synced, none with a percentage
          </div>
          <p className={`${T.meta} text-red-800 mt-1`}>
            Nothing VAT-bearing can post until this is fixed. Sage serves a summary of each rate unless the
            full record is asked for, and the summary leaves the percentage out. Press
            {' '}<strong>Refresh from Sage</strong>: the sync now asks for the full record and falls back to
            fetching each rate on its own. If it is still empty afterwards, press <strong>Diagnose</strong> and
            send the <code>/tax_rates</code> output over.
          </p>
        </div>
      )}

      {/* Percentages that came out of a name rather than a field. Correct in
          almost every case, and worth one look from a person. */}
      {status.connected && (counts.tax_rates_from_name ?? 0) > 0 && (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-2">
          <div className={`${T.strong} text-amber-900`}>
            {counts.tax_rates_from_name} tax {counts.tax_rates_from_name === 1 ? 'rate has' : 'rates have'} a
            percentage read from the rate's name
          </div>
          <p className={`${T.meta} text-amber-800 mt-1`}>
            Sage returned no percentage field for {counts.tax_rates_from_name === 1 ? 'it' : 'them'}, so it was
            taken from the display name (for example "Standard 20.00%"). That is almost always right, and it is
            a weaker fact than a field. The rates are listed below with their source: check those marked
            "read from name" against Sage once and it need not be looked at again.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Tax rates ───────────────────────────────────────────────────────────────
// The rates themselves, with where each percentage came from.
//
// These were invisible until an invoice was refused for want of one, which is
// the worst possible moment to discover that a rate synced without a number
// against it. Every VAT-bearing invoice depends on this handful of rows, so
// they are shown, and each says how sure it is.
const RATE_SOURCE = {
  list:            { label: 'from Sage',              cls: 'text-emerald-700' },
  detail:          { label: 'from Sage',              cls: 'text-emerald-700' },
  name:            { label: 'read from name',         cls: 'text-amber-700'  },
  'assumed-zero':  { label: 'exempt, taken as 0%',    cls: 'text-amber-700'  },
};

function TaxRates({ rates }) {
  if (!rates?.length) return null;
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200">
        <span className={`${T.label} text-gray-500`}>Sage tax rates</span>
      </div>
      <div className="divide-y divide-gray-100">
        {rates.map(r => {
          const src = RATE_SOURCE[r.percentage_source] || null;
          return (
            <div key={r.id} className="px-3 py-1.5 flex items-center justify-between gap-3">
              <span className={`${T.body} text-gray-700 truncate`}>{r.name || r.id}</span>
              <span className="flex items-center gap-2 flex-shrink-0">
                {src && <span className={`${T.meta} ${src.cls}`}>{src.label}</span>}
                {r.percentage != null ? (
                  <span className={`${T.body} font-bold tabular-nums text-navy`}>{r.percentage}%</span>
                ) : (
                  <span className={`${T.meta} font-semibold text-red-700`}>no percentage</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Diagnostics ─────────────────────────────────────────────────────────────
// The raw response from each reference endpoint. Ugly on purpose: this is
// evidence, and summarising it would throw away the thing that makes it useful.
function Diagnostics({ probes, onClose }) {
  if (!probes) return null;
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <span className={`${T.label} text-gray-500`}>What Sage actually returned</span>
        <button onClick={onClose} className={`${T.meta} text-gray-400 hover:text-gray-700`}>close</button>
      </div>
      <div className="p-3 space-y-2">
        {probes.map(p => (
          <div key={p.path}>
            <div className={`${T.meta} font-semibold ${p.ok ? 'text-emerald-700' : 'text-red-700'}`}>
              {p.path} — {p.ok ? `HTTP ${p.status || 200}` : `failed: ${p.error || p.status}`}
            </div>
            <pre className="mt-1 bg-gray-50 border border-gray-200 rounded p-2 text-[9px] overflow-x-auto max-h-52">
{p.raw || '(no body)'}
            </pre>
          </div>
        ))}
        <p className={`${T.meta} text-gray-500`}>
          If a column on this screen is empty but the field is clearly present above, the sync is reading the
          wrong field name. Send this output over and it is a one-line fix.
        </p>
      </div>
    </div>
  );
}

// ── Supplier mapping ────────────────────────────────────────────────────────
//
// What the mapping is FOR: Sage's purchase-invoice endpoint takes a contact_id,
// not a supplier name. Without it there is nothing to post an invoice against,
// and guessing the supplier from a name is how an invoice ends up on the wrong
// account. So the link is required, but doing 139 of them by hand is not: the
// matcher proposes, a person accepts, and anything it is unsure about is left
// alone rather than guessed.
function SupplierMapping({ data: refData, onMap, onRoute, onLedger, defaultRoute, onFlash }) {
  const [creatingFor, setCreatingFor] = useState(null);
  const [matches, setMatches]   = useState(null);
  const [chosen, setChosen]     = useState({});
  const [busy, setBusy]         = useState(false);
  const [query, setQuery]       = useState('');
  const [onlyInvoiced, setOnlyInvoiced] = useState(true);

  const contacts = refData?.contacts || [];
  // A contact that Sage does not class as a supplier is still selectable — a
  // contact can genuinely be both — but it is labelled, because linking a
  // supplier to a customer account would post purchase invoices against it.
  const contactOptions = useMemo(() => contacts.map(c => {
    const isVendor = !c.contact_type || c.contact_type === 'unknown' || c.contact_type.includes('vendor');
    return {
      value: c.id,
      label: `${c.name}${c.reference ? ` (${c.reference})` : ''}${isVendor ? '' : ' — customer, not a supplier'}`,
    };
  }), [contacts]);
  const nonVendorCount = contacts.filter(c => c.contact_type && c.contact_type !== 'unknown' && !c.contact_type.includes('vendor')).length;

  // The nominal code each supplier's invoices usually go to. Set from an invoice
  // as it is coded, and seen and changed here — one supplier's usual code was
  // otherwise only visible on an invoice from that supplier, which is no way to
  // review or correct the set of them.
  const ledgerPickerOptions = useMemo(() => (refData?.ledger_accounts || []).map(l => ({
    value: l.id,
    label: `${l.nominal_code ? `${l.nominal_code} — ` : ''}${l.display_name || l.name || ''}`,
  })), [refData?.ledger_accounts]);

  const findMatches = async () => {
    setBusy(true);
    try {
      const r = await api.get('sage/supplier-matches');
      setMatches(r);
      // Exact and strong matches start ticked; weak ones do not. A weak match is
      // a suggestion to look at, not a default to accept by pressing Apply.
      const pre = {};
      for (const m of r.matches || []) {
        if (m.sage_contact_id && (m.confidence === 'exact' || m.confidence === 'strong')) pre[m.supplier_id] = true;
      }
      setChosen(pre);
    } catch (e) { onFlash('error', e.message || 'Could not work out the matches.'); }
    finally { setBusy(false); }
  };

  const applyChosen = async () => {
    const picked = (matches?.matches || []).filter(m => chosen[m.supplier_id] && m.sage_contact_id);
    if (!picked.length) return;
    setBusy(true);
    try {
      const r = await api.post('sage/supplier-matches', {
        matches: picked.map(m => ({ supplier_id: m.supplier_id, sage_contact_id: m.sage_contact_id })),
      });
      onFlash('ok', `Linked ${r.applied} supplier${r.applied === 1 ? '' : 's'} to Sage.`);
      setMatches(null); setChosen({});
      await onMap();
    } catch (e) { onFlash('error', e.message || 'Could not save those links.'); }
    finally { setBusy(false); }
  };

  const suppliers = refData?.suppliers || [];
  const shown = suppliers.filter(s => {
    if (onlyInvoiced && !(s.invoice_count > 0)) return false;
    if (query.trim() && !s.name.toLowerCase().includes(query.trim().toLowerCase())) return false;
    return true;
  });
  const mappedCount = suppliers.filter(s => s.sage_contact_id).length;
  const invoicedUnmapped = suppliers.filter(s => s.invoice_count > 0 && !s.sage_contact_id).length;

  const CONFIDENCE = {
    exact:  { label: 'exact',  cls: 'bg-emerald-100 text-emerald-800' },
    strong: { label: 'strong', cls: 'bg-blue-100 text-blue-800' },
    weak:   { label: 'check',  cls: 'bg-amber-100 text-amber-800' },
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className={`${T.label} text-gray-500`}>Supplier: Sage contact, usual nominal code, route</span>
          <span className={`${T.meta} text-gray-500`}>{mappedCount} of {suppliers.length} linked</span>
        </div>
        <p className={`${T.meta} text-gray-500 mt-1`}>
          Sage posts against a contact id, not a name, so an invoice cannot reach the ledger until its supplier is
          linked. Only the {invoicedUnmapped > 0 ? <strong>{invoicedUnmapped}</strong> : 'zero'} supplier
          {invoicedUnmapped === 1 ? '' : 's'} you have actually invoiced and not yet linked block anything.
        </p>
        {nonVendorCount > 0 && (
          <p className={`${T.meta} text-amber-800 mt-1`}>
            {nonVendorCount} of the {contacts.length} contacts Sage returned are customers rather than suppliers, so
            the supplier filter on the request is not being applied. They are labelled in the picker and are never
            proposed automatically, because a purchase invoice must not be posted against a customer account.
          </p>
        )}
      </div>

      <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 flex-wrap">
        <button onClick={findMatches} disabled={busy} className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}>
          {busy && !matches ? 'Matching…' : 'Find matches automatically'}
        </button>
        <input className={`${sel} flex-1 min-w-[10rem]`} placeholder="Search suppliers…"
          value={query} onChange={e => setQuery(e.target.value)} />
        <label className={`${T.meta} text-gray-600 flex items-center gap-1`}>
          <input type="checkbox" checked={onlyInvoiced} onChange={e => setOnlyInvoiced(e.target.checked)} />
          Only ones we have invoiced
        </label>
      </div>

      {matches && (
        <div className="px-3 py-2 border-b border-gray-100 bg-blue-50/40">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
            <span className={`${T.strong} text-navy`}>
              {(matches.matches || []).length} proposed, {(matches.unmatched || []).length} with no match
            </span>
            <div className="flex gap-1.5">
              <button onClick={applyChosen} disabled={busy}
                className={`${btn} bg-navy text-white border-navy hover:bg-navy/90`}>
                Apply {Object.values(chosen).filter(Boolean).length} ticked
              </button>
              <button onClick={() => { setMatches(null); setChosen({}); }} className={`${btn} border-gray-300 text-gray-600 bg-white`}>
                Discard
              </button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto rounded border border-blue-100 bg-white">
            {(matches.matches || []).map(m => (
              <label key={m.supplier_id}
                className="flex items-center gap-2 px-2 py-1 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-blue-50/50">
                <input type="checkbox" disabled={!m.sage_contact_id}
                  checked={!!chosen[m.supplier_id]}
                  onChange={e => setChosen(c => ({ ...c, [m.supplier_id]: e.target.checked }))} />
                <span className={`${T.body} text-gray-800 flex-1 truncate`}>{m.supplier_name}</span>
                <span className={`${T.meta} text-gray-400`}>→</span>
                <span className={`${T.body} text-navy flex-1 truncate`}>{m.sage_contact_name || m.reason}</span>
                {m.invoice_count > 0 && (
                  <span className={`${T.meta} text-gray-500 tabular-nums`}>{m.invoice_count} inv</span>
                )}
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${CONFIDENCE[m.confidence]?.cls || 'bg-gray-100 text-gray-600'}`}>
                  {CONFIDENCE[m.confidence]?.label || m.confidence}
                </span>
              </label>
            ))}
            {!(matches.matches || []).length && (
              <div className={`${T.meta} text-gray-500 px-2 py-2`}>Nothing matched by name. Link them by hand below.</div>
            )}
          </div>
        </div>
      )}

      {creatingFor && (
        <CreateSupplierDialog supplier={creatingFor}
          onClose={() => setCreatingFor(null)}
          onLinkExisting={async (supplierId, contactId) => { await onMap(supplierId, contactId); setCreatingFor(null); }}
          onCreated={async () => { setCreatingFor(null); onFlash('ok', 'Created in Sage and linked.'); await onMap(); }} />
      )}

      <div className="max-h-96 overflow-y-auto overflow-x-auto">
        <table className="w-full">
          <tbody className="divide-y divide-gray-100">
            {shown.map(s => (
              <tr key={s.id} className={s.sage_contact_id ? '' : 'bg-amber-50/40'}>
                <td className={`${T.body} text-gray-800 px-3 py-1.5 w-1/2`}>
                  {s.name}
                  {s.invoice_count > 0 && (
                    <span className={`${T.meta} text-gray-400 ml-1.5`}>{s.invoice_count} invoice{s.invoice_count === 1 ? '' : 's'}</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <SearchPicker options={contactOptions} value={s.sage_contact_id || ''}
                    onChange={v => onMap(s.id, v)} className={`${sel} w-full`} placeholder="Search Sage contacts…" />
                  {/* A supplier that is not in Sage cannot be posted against at
                      all. Creating one is a write to the accounting system, so
                      it goes through a preview that shows what would be created
                      and anything it might duplicate. */}
                  {!s.sage_contact_id && (
                    <button onClick={() => setCreatingFor(s)}
                      className={`${T.meta} text-navy hover:underline mt-0.5`}>
                      Not in Sage? Create it
                    </button>
                  )}
                </td>
                {/* The supplier's usual nominal code. Sage refuses a purchase
                    invoice without one, and typing the same code for the same
                    supplier every month is the work this removes: set it once
                    and every invoice from them arrives coded, still overridable
                    on the invoice itself. */}
                <td className="px-3 py-1.5 w-56">
                  <SearchPicker options={ledgerPickerOptions} value={s.default_sage_ledger_account_id || ''}
                    onChange={v => onLedger(s.id, v)} className={`${sel} w-full`}
                    placeholder="Usual nominal code…" />
                  {!s.default_sage_ledger_account_id && s.invoice_count > 0 && (
                    <div className={`${T.meta} text-amber-800 mt-0.5`}>
                      No usual code — each invoice has to be coded by hand.
                    </div>
                  )}
                </td>
                {/* The cutover, one supplier at a time. Moving everybody over in
                    one go means discovering a problem across every supplier at
                    once, with AutoEntry already switched off. */}
                <td className="px-3 py-1.5 w-40">
                  <select className={`${sel} w-full`} value={s.sage_posting_route || ''}
                    onChange={e => onRoute(s.id, e.target.value)}
                    disabled={!s.sage_contact_id}
                    title={s.sage_contact_id ? 'Where this supplier\'s invoices go' : 'Link the supplier to a Sage contact first'}>
                    <option value="">Default ({defaultRoute === 'ops' ? 'post here' : 'AutoEntry'})</option>
                    <option value="autoentry">AutoEntry</option>
                    <option value="ops">Post from here</option>
                  </select>
                </td>
              </tr>
            ))}
            {!shown.length && (
              <tr><td className={`${T.meta} text-gray-500 px-3 py-3`} colSpan={4}>
                {onlyInvoiced ? 'Every supplier you have invoiced is linked.' : 'No suppliers match that search.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Handover and reconciliation ─────────────────────────────────────────────
//
// While AutoEntry and ops posting run side by side, the only question that
// matters is "is every invoice in the ledger exactly once". Answering it needs
// all the outgoing money in one place and a route recorded against each invoice,
// or the two systems silently double up.
//
// The two invoice streams that cannot reach Sage at all are counted separately
// rather than folded into the totals. A reconciliation that quietly omits what
// it cannot handle is how money goes missing.
const STATE_LABELS = {
  in_sage:            { label: 'In Sage',              cls: 'bg-emerald-100 text-emerald-800' },
  with_autoentry:     { label: 'Sent to AutoEntry',    cls: 'bg-blue-100 text-blue-800' },
  awaiting_autoentry: { label: 'For AutoEntry',        cls: 'bg-slate-100 text-slate-700' },
  awaiting_ops:       { label: 'To post from here',    cls: 'bg-amber-100 text-amber-800' },
  keyed_by_hand:      { label: 'Keyed into Sage',      cls: 'bg-slate-100 text-slate-700' },
  excluded:           { label: 'Not going to Sage',    cls: 'bg-slate-100 text-slate-500' },
};

function Reconciliation({ onFlash }) {
  const [data, setData]   = useState(null);
  const [months, setMonths] = useState(3);
  const [filter, setFilter] = useState('all');
  const [attaching, setAttaching] = useState(false);

  const load = useCallback(async (m) => {
    try { setData(await api.get('sage/reconciliation', { months: m })); }
    catch (e) { onFlash('error', e.message || 'Could not load the reconciliation.'); }
  }, [onFlash]);
  useEffect(() => { load(months); }, [months, load]);

  // Attach the missing paperwork in bulk. Every invoice posted while the Sage
  // attachment call was being refused is in the ledger with nothing behind it,
  // and finding each one in the inbox by hand is the sort of job that does not
  // get done. Only invoices already in Sage with a document stored here and no
  // attachment are touched, so pressing it twice is harmless.
  const backfillAttachments = async () => {
    setAttaching(true);
    try {
      const r = await api.post('sage/attachments/backfill', { limit: 25 });
      const failed = r.results?.find(x => !x.ok);
      if (r.attached) onFlash('success', `Attached ${r.attached} invoice${r.attached === 1 ? '' : 's'} in Sage.${r.failed ? ` ${r.failed} failed: ${failed?.error || ''}` : ''}`);
      else onFlash('error', failed?.error || 'Nothing was attached.');
      await load(months);
    } catch (e) {
      onFlash('error', e.message || 'Could not attach the invoices.');
    } finally { setAttaching(false); }
  };

  if (!data) return null;
  const s = data.summary;
  const rows = (data.rows || []).filter(r => filter === 'all' || r.state === filter);
  const notWired = (data.not_wired?.contractor?.count || 0) + (data.not_wired?.purchase_order?.count || 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>Where every invoice stands</span>
        <select className={sel} value={months} onChange={e => setMonths(Number(e.target.value))}>
          {[1, 3, 6, 12].map(m => <option key={m} value={m}>Last {m} month{m === 1 ? '' : 's'}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3">
        {[
          ['in_sage', 'In Sage', 'text-emerald-700'],
          ['with_autoentry', 'Sent to AutoEntry', 'text-blue-700'],
          ['awaiting_ops', 'To post from here', 'text-amber-700'],
          ['awaiting_autoentry', 'For AutoEntry', 'text-gray-600'],
        ].map(([k, label, tone]) => (
          <button key={k} onClick={() => setFilter(f => f === k ? 'all' : k)}
            className={`text-left bg-white rounded border px-2 py-1.5 hover:bg-gray-50 ${filter === k ? 'border-navy' : 'border-gray-200'}`}>
            <div className={`${T.label} text-gray-500`}>{label}</div>
            <div className={`text-[16px] font-bold tabular-nums ${tone}`}>{s?.[k]?.count ?? 0}</div>
            <div className={`${T.meta} text-gray-400`}>{money(s?.[k]?.value)}</div>
          </button>
        ))}
      </div>

      {/* A ledger entry with no paperwork behind it is the VAT reclaim at risk,
          so it gets its own line rather than being buried in a status column. */}
      {s?.missing_attachment > 0 && (
        <div className={`${T.meta} text-amber-800 bg-amber-50 px-3 py-1.5 border-y border-amber-200 flex items-start justify-between gap-2 flex-wrap`}>
          <span>
            {s.missing_attachment} invoice{s.missing_attachment === 1 ? ' is' : 's are'} in Sage with a document stored here
            but no attachment against the ledger entry. Without it there is nothing to produce if the reclaim is queried.
          </span>
          <button onClick={backfillAttachments} disabled={attaching}
            className="px-2 py-0.5 rounded border border-amber-300 bg-white text-amber-900 font-semibold hover:bg-amber-100 disabled:opacity-40 whitespace-nowrap">
            {attaching ? 'Attaching…' : 'Attach them now'}
          </button>
        </div>
      )}

      {notWired > 0 && (
        <p className={`${T.meta} text-gray-600 bg-gray-50 px-3 py-1.5 border-y border-gray-200`}>
          Not counted above, because nothing here can post them to Sage yet:{' '}
          <strong>{data.not_wired.contractor.count}</strong> contractor invoices ({money(data.not_wired.contractor.value)})
          and <strong>{data.not_wired.purchase_order.count}</strong> PO invoices ({money(data.not_wired.purchase_order.value)}).
          Those still go through whatever route they use today.
        </p>
      )}

      <div className="max-h-80 overflow-y-auto overflow-x-auto border-t border-gray-100">
        <table className="w-full">
          <tbody className="divide-y divide-gray-100">
            {rows.slice(0, 200).map(r => {
              const cfg = STATE_LABELS[r.state] || { label: r.state, cls: 'bg-gray-100 text-gray-600' };
              return (
                <tr key={r.id}>
                  <td className="px-3 py-1.5">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${cfg.cls}`}>{cfg.label}</span>
                  </td>
                  <td className={`${T.body} px-3 py-1.5`}>
                    <div className="text-gray-800">{r.payee || 'Unknown'}</div>
                    <div className={`${T.meta} text-gray-400`}>
                      {r.invoice_number || 'no number'}{r.invoice_date ? ` · ${formatDate(r.invoice_date)}` : ''}
                      {r.sage_invoice_number ? ` · Sage ${r.sage_invoice_number}` : ''}
                    </div>
                  </td>
                  <td className={`${T.body} px-3 py-1.5 text-right tabular-nums text-gray-600 whitespace-nowrap`}>
                    {money(r.gross)}
                    {r.missing_attachment && <div className={`${T.meta} text-amber-700`}>no PDF in Sage</div>}
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={3} className={`${T.body} text-gray-500 px-3 py-4 text-center`}>Nothing in this period.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Creating a supplier in Sage ─────────────────────────────────────────────
//
// Shown before anything is written, because a contact is a permanent record in
// the accounting system. The important part of this dialog is not the fields:
// it is the list of contacts that already look like this supplier. Creating a
// near-duplicate is the failure mode, and it is only avoidable if the person
// deciding can see what they might be duplicating.
function CreateSupplierDialog({ supplier, onClose, onCreated, onLinkExisting }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    api.get(`sage/supplier/${supplier.id}/create-preview`)
      .then(setPreview)
      .catch(e => setErr(e.message || 'Could not work out what would be created.'));
  }, [supplier.id]);

  const create = async (confirmNotExisting = false) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`sage/supplier/${supplier.id}/create`, { confirm_not_existing: confirmNotExisting });
      onCreated(r);
    } catch (e) {
      setErr(e.message || 'Sage refused to create the contact.');
    } finally { setBusy(false); }
  };

  const c = preview?.payload?.contact;
  const looksExisting = (preview?.possible_existing || []).length > 0;

  return (
    <Modal
      title="Create in Sage"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className={`${btn} border-gray-300 text-gray-600 bg-white`}>Cancel</button>
          <button onClick={() => create(looksExisting)} disabled={busy || !preview || preview.problems?.length}
            className={`${btn} bg-navy text-white border-navy hover:bg-navy/90`}>
            {busy ? 'Creating…' : looksExisting ? 'Create anyway — it is a different company' : 'Create in Sage'}
          </button>
        </>
      }
    >
        <p className={`${T.meta} text-gray-400 mb-3`}>{supplier.name}</p>
        <div className="space-y-3">
          {err && <div className={`${T.body} px-3 py-2 rounded border border-red-200 bg-red-50 text-red-800`}>{err}</div>}

          {looksExisting && (
            <div className="px-3 py-2 rounded border border-amber-300 bg-amber-50">
              <div className={`${T.strong} text-amber-900`}>Sage may already have this supplier</div>
              {preview.possible_existing.map(p => (
                <div key={p.id} className={`${T.body} text-amber-900 mt-1`}>
                  <strong>{p.name}</strong> <span className={`${T.meta}`}>({p.confidence} match — {p.reason})</span>
                  <button onClick={() => onLinkExisting(supplier.id, p.id)}
                    className={`${btn} ml-2 border-amber-400 text-amber-900 bg-white hover:bg-amber-100`}>
                    Link to this instead
                  </button>
                </div>
              ))}
            </div>
          )}

          {preview && !preview.possible_existing.length && (
            <div className={`${T.meta} text-gray-500`}>
              Checked {preview.searched} Sage supplier contacts and found nothing that looks like this one.
            </div>
          )}

          {c && (
            <div className="rounded border border-gray-200 divide-y divide-gray-100">
              {[
                ['Name', c.name],
                ['Type', 'Supplier'],
                ['Reference', c.reference || '—'],
                ['VAT number', c.tax_number || '—'],
                ['Email', c.email || '—'],
                ['Address', c.main_address
                  ? [c.main_address.address_line_1, c.main_address.address_line_2, c.main_address.city, c.main_address.postal_code].filter(Boolean).join(', ')
                  : '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-2 px-2 py-1">
                  <span className={`${T.label} text-gray-500 w-24 flex-shrink-0 pt-0.5`}>{k}</span>
                  <span className={`${T.body} text-gray-800`}>{v}</span>
                </div>
              ))}
            </div>
          )}

          <p className={`${T.meta} text-gray-500`}>
            Only what ops actually holds is sent. Anything missing can be filled in inside Sage afterwards —
            inventing it here would put a guess into the accounting system that later reads as a decision.
          </p>
          {(preview?.problems || []).map((p, i) => (
            <div key={i} className={`${T.body} text-red-700`}>✕ {p}</div>
          ))}
        </div>

    </Modal>
  );
}

// ── Staying in step with Sage ───────────────────────────────────────────────
//
// A single successful post does not make two systems "in sync". The Sage entry
// can be edited, or deleted, and until somebody looks, ops carries on believing
// what was true at the moment it posted. This reads posted invoices back and
// reports drift; it never repairs anything, because an automatic correction to a
// ledger entry should need a person.
// ── Our copy of Sage's purchase ledger ──────────────────────────────────────
//
// "Is this invoice already in Sage?" used to ask Sage live, and the answer took
// minutes or never arrived at all. It now reads a local copy, refreshed hourly, so
// the answer is immediate — and the price of that is staleness, which is why this
// panel exists: the copy has to be able to say what it covers, because the sentence
// it supports is "not in Sage", and the next action after that sentence is to post.
// ── Where the ledger starts caring ──────────────────────────────────────────
//
// "Can we have an option to just start from yesterday and not really care about
// previous unmatched — otherwise we might have to go back several years!"
//
// 235 outstanding invoices going back years is not a queue anybody works through. It is
// a number that gets ignored, and a number that gets ignored takes the genuine ones
// down with it. Everything before ops went live is already in Sage anyway, keyed by
// hand, so the honest thing is to say so once rather than ask about each one.
//
// It uses the existing exclusion mechanism rather than inventing a new kind of limbo:
// the invoices stay visible, stay searchable, can still be linked to their Sage entry at
// any time, and nothing pretends they were posted from here. They just stop being asked
// for.
function SageCutover({ onFlash }) {
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(yesterday);
  const [current, setCurrent] = useState(null);
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const c = await api.get('procurement/supplier-invoices/sage-cutover');
      setCurrent(c?.from_date || null);
      setCounts(c || null);
    } catch { /* the panel just stays quiet */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    if (!window.confirm(
      `Stop asking for invoices dated before ${from}?\n\n`
      + 'They are recorded as deliberately not going to Sage, with the reason, because they are already there from '
      + 'the old process. Nothing is deleted, they stay searchable, and any one of them can still be linked to its '
      + 'Sage entry or put back on the list.')) return;
    setBusy(true);
    try {
      const r = await api.post('procurement/supplier-invoices/sage-cutover', { from_date: from });
      onFlash('ok', `${r.excluded.toLocaleString('en-GB')} invoice${r.excluded === 1 ? '' : 's'} before ${r.from_date} set aside. ${r.remaining.toLocaleString('en-GB')} still to get into Sage.`);
      await load();
    } catch (e) { onFlash('error', e.message || 'Could not set the cutover.'); }
    finally { setBusy(false); }
  };

  const lift = async () => {
    if (!window.confirm('Put every invoice the cutover set aside back on the list?')) return;
    setBusy(true);
    try {
      const r = await api.delete('procurement/supplier-invoices/sage-cutover');
      onFlash('ok', `${r.reopened.toLocaleString('en-GB')} invoice${r.reopened === 1 ? '' : 's'} back on the list.`);
      await load();
    } catch (e) { onFlash('error', e.message || 'Could not lift the cutover.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>Where posting to Sage starts</span>
        {current
          ? <span className={`${T.meta} text-gray-500`}>Currently from <strong>{current}</strong></span>
          : <span className={`${T.meta} text-amber-800`}>No cutover — every logged invoice is being asked for</span>}
      </div>
      <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
        Invoices dated before this are recorded as deliberately not going to Sage, because they are already in Sage
        from the old process. They stay visible and can still be linked to their entry; they just stop being chased.
        Anything logged later but dated before the cutover is treated the same way, so the backlog cannot quietly
        reopen.
      </p>
      <div className="px-3 pb-2 flex items-end gap-2 flex-wrap">
        <label className="block">
          <span className={`${T.label} text-gray-400 block`}>Start from</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={sel} />
        </label>
        <button onClick={apply} disabled={busy || !from}
          className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>
          {busy ? 'Working…' : 'Set the cutover'}
        </button>
        {current && (
          <button onClick={lift} disabled={busy}
            className={`${btn} border-gray-300 text-gray-500 bg-white hover:bg-gray-50`}>
            Lift it
          </button>
        )}
        {counts && (
          <span className={`${T.meta} text-gray-500 ml-auto`}>
            {Number(counts.outstanding || 0).toLocaleString('en-GB')} still to get into Sage
            {counts.set_aside ? `, ${Number(counts.set_aside).toLocaleString('en-GB')} set aside by a cutover` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

function LedgerCache({ onFlash }) {
  const [cache, setCache] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try { setCache(await api.get('sage/cache')); setError(null); }
    catch (e) { setError(e.message || 'Could not read the cache state.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const p = cache?.progress || {};
  const running = p.state === 'running';

  // While the fill is running this polls, so the figures move on their own. The work
  // itself is done by the worker on a three-minute tick, so closing this page does not
  // stop it — which is the difference between a job and a button.
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);

  // Pressing this arms the job and runs the first batch, so the answer here is a real
  // one — including the real error if Sage or the database refuses.
  const start = async (restart = false) => {
    if (restart && !window.confirm('Walk the whole of Sage again from the beginning?\n\nNothing is duplicated (entries are matched on their Sage id) and nothing is lost. It just costs the requests again.')) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post('sage/cache/backfill', { restart });
      setCache(r.cache || null);
      const first = r.first || {};
      if (first.errors?.length) onFlash('error', first.errors.join(' '));
      else onFlash('ok', `Fill started — ${(first.written || 0).toLocaleString('en-GB')} entries in so far. It carries on by itself every ${p.tick_minutes || 3} minutes.`);
    } catch (e) {
      setError(e.message || 'Could not start the fill.');
      onFlash('error', e.message || 'Could not start the fill.');
    } finally { setBusy(false); }
  };

  // One more batch, now, for somebody who would rather not wait for the tick.
  const nudge = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.post('sage/cache/sync', { mode: 'auto', max_pages: 20 });
      setCache(r.cache || null);
      if (r.busy) onFlash('warn', 'A sync was already running — left it to get on with it.');
      else if (r.errors?.length) onFlash('error', r.errors.join(' '));
      else onFlash('ok', `${(r.written || 0).toLocaleString('en-GB')} entr${r.written === 1 ? 'y' : 'ies'} pulled over ${r.pages || 0} page${r.pages === 1 ? '' : 's'}.`);
    } catch (e) {
      setError(e.message || 'The sync failed.');
      onFlash('error', e.message || 'The sync failed.');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.post('sage/cache/verify', {});
      setCache(r.cache || null);
      if (r.error) onFlash('error', r.error);
      else if (r.short) onFlash('warn', `Sage holds ${r.sage_total.toLocaleString('en-GB')} and ${r.held.toLocaleString('en-GB')} are cached — ${r.short.toLocaleString('en-GB')} missing. The fill has been restarted.`);
      else onFlash('ok', `Verified: Sage holds ${r.sage_total.toLocaleString('en-GB')} purchase invoices and all ${r.held.toLocaleString('en-GB')} are cached.`);
    } catch (e) {
      setError(e.message || 'Could not verify against Sage.');
      onFlash('error', e.message || 'Could not verify against Sage.');
    } finally { setBusy(false); }
  };

  const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-GB'));
  const ready = !!cache?.ready;
  const verified = cache?.verified || {};
  const schemaBad = cache?.schema && cache.schema.ok === false;
  // At a batch every tick, this is how much longer the rest of it takes. An estimate
  // stated as an estimate beats no idea at all.
  const minutesLeft = p.pages_left != null && p.per_run
    ? Math.max(1, Math.ceil(p.pages_left / p.per_run) * (p.tick_minutes || 3))
    : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>Our copy of Sage's purchase ledger</span>
        <div className="flex gap-1.5 flex-wrap">
          {p.state !== 'complete' && (
            <button onClick={() => start(false)} disabled={busy}
              className={`${btn} border-navy bg-navy text-white hover:bg-navy/90`}>
              {busy ? 'Working…' : running ? 'Run the next batch now' : 'Start the full sync'}
            </button>
          )}
          {p.state === 'complete' && (
            <>
              <button onClick={nudge} disabled={busy} className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}>
                {busy ? 'Working…' : 'Sync changes now'}
              </button>
              <button onClick={verify} disabled={busy} className={`${btn} border-gray-300 text-gray-700 bg-white hover:bg-gray-50`}>
                Re-check against Sage
              </button>
              <button onClick={() => start(true)} disabled={busy} className={`${btn} border-gray-300 text-gray-500 bg-white hover:bg-gray-50`}>
                Re-sync everything
              </button>
            </>
          )}
        </div>
      </div>
      <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
        Read-only, refreshed hourly, and never written back: Sage stays the source of truth. It is what makes
        "already in Sage?" answer in a moment instead of minutes, and linking an invoice still confirms the entry
        with Sage at the moment it is linked.
      </p>

      {/* The failure that has to be unmistakable. A column this code writes that the
          database has not got makes every write fail, and that used to show as a cache
          that had simply never synced — so the button looked broken with no way to tell
          why. */}
      {schemaBad && (
        <div className={`${T.body} mx-3 mb-2 px-2 py-1.5 rounded border border-red-300 bg-red-50 text-red-800`}>
          <div className={`${T.strong} text-red-900`}>The database is missing what this needs</div>
          {cache.schema.problems.map(x => <div key={x} className={`${T.meta} mt-0.5`}>· {x}</div>)}
          <div className={`${T.meta} mt-1`}>{cache.schema.hint}</div>
        </div>
      )}

      {error && (
        <div className={`${T.meta} mx-3 mb-2 px-2 py-1.5 rounded border border-red-300 bg-red-50 text-red-800`}>{error}</div>
      )}

      {cache && !schemaBad && (
        <>
          {/* ── PROGRESS ─────────────────────────────────────────────────────── */}
          {p.state !== 'complete' && (
            <div className={`mx-3 mb-2 px-2 py-2 rounded border ${
              p.state === 'failed' ? 'border-red-300 bg-red-50' : 'border-blue-300 bg-blue-50'}`}>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <span className={`${T.strong} ${p.state === 'failed' ? 'text-red-900' : 'text-blue-900'}`}>
                  {p.state === 'failed' ? 'The full sync stopped'
                    : p.state === 'running' ? (p.running_now ? 'Filling now…' : 'Filling — next batch within a few minutes')
                    : 'The full sync has not been started'}
                </span>
                <span className={`${T.meta} ${p.state === 'failed' ? 'text-red-800' : 'text-blue-800'}`}>
                  {n(p.held)}{p.expected != null ? ` of ${n(p.expected)}` : ''} entries
                  {p.percent != null ? ` · ${p.percent}%` : ''}
                </span>
              </div>
              {/* A bar, because "1,400 of 4,182" is a number and this is a picture of
                  the same thing. Indeterminate while Sage has not yet told us the
                  total, rather than pretending to a percentage. */}
              <div className="mt-1 h-1.5 rounded-full bg-white/70 overflow-hidden border border-blue-200">
                <div className={`h-full ${p.state === 'failed' ? 'bg-red-400' : 'bg-navy'} transition-all duration-500`}
                  style={{ width: p.percent != null ? `${Math.max(2, p.percent)}%` : '15%' }} />
              </div>
              <div className={`${T.meta} mt-1 ${p.state === 'failed' ? 'text-red-800' : 'text-blue-800'}`}>
                {p.pages_done ? `${n(p.pages_done)} page${p.pages_done === 1 ? '' : 's'} of ${n(p.batch_size)} read. ` : ''}
                {p.pages_left != null && p.state === 'running'
                  ? `About ${n(p.pages_left)} to go — roughly ${minutesLeft} minute${minutesLeft === 1 ? '' : 's'} at ${p.per_run} pages every ${p.tick_minutes} minutes. `
                  : ''}
                {p.state === 'running'
                  ? 'It runs itself to the end on a three-minute tick, so you can leave this page.'
                  : p.state === 'failed'
                    ? `Stopped after ${p.failures} refusals from Sage. Fix the cause and press Start the full sync — it resumes at page ${n((p.pages_done || 0) + 1)} rather than starting over.`
                    : 'Nothing has been pulled from Sage yet, so "already in Sage?" cannot answer.'}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 pb-2">
            {[
              ['Entries held', n(cache.entries)],
              ['Covers', cache.oldest_date && cache.newest_date ? `${cache.oldest_date} → ${cache.newest_date}` : '—'],
              ['Last synced', cache.last_sync_at
                ? `${cache.age_hours != null && cache.age_hours < 1 ? 'under 1' : cache.age_hours}h ago${cache.last_sync_kind ? ` (${cache.last_sync_kind})` : ''}`
                : 'never'],
              ['Sage reports', n(cache.sage_reported_total)],
            ].map(([label, value]) => (
              <div key={label} className="rounded border border-gray-200 px-2 py-1.5">
                <div className={`${T.label} text-gray-400`}>{label}</div>
                <div className={`${T.body} font-semibold text-gray-800`}>{value}</div>
              </div>
            ))}
          </div>

          {/* ── THE SUCCESS INDICATOR ────────────────────────────────────────────
              Not "the job finished" but "Sage says it holds this many and we hold
              them all, checked at this time". That is the sentence that licenses the
              cache to report an invoice as absent. */}
          <div className={`mx-3 mb-2 px-2 py-1.5 rounded border ${verified.ok && ready
            ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
            <div className={`${T.strong} ${verified.ok && ready ? 'text-emerald-900' : 'text-amber-900'}`}>
              {verified.ok && ready
                ? 'Verified against Sage — safe to report an invoice as NOT in Sage'
                : 'Not yet in a position to say an invoice is missing'}
            </div>
            <p className={`${T.meta} ${verified.ok && ready ? 'text-emerald-800' : 'text-amber-800'} mt-0.5`}>
              {verified.at
                ? `Sage reported ${n(verified.sage_total)} purchase invoices and ${n(verified.held)} were cached, checked ${verified.at} UTC. `
                : 'Sage has not been asked for its own count yet. '}
              {verified.error ? `${verified.error} ` : ''}
              {!ready && cache.reason ? `${cache.reason[0].toUpperCase()}${cache.reason.slice(1)}. ` : ''}
              {verified.ok && ready
                ? 'Searches that find nothing are reported as "not in Sage".'
                : 'Until then a search that finds nothing says "cannot tell yet" rather than "not in Sage" — posting something the ledger already holds is the duplicate this exists to prevent.'}
            </p>
            {cache.thin_entries > 0 && (
              <p className={`${T.meta} text-amber-800 mt-0.5`}>
                {n(cache.thin_entries)} entr{cache.thin_entries === 1 ? 'y' : 'ies'} came back from Sage as a summary
                with no amount. They are read in full a batch at a time; an entry with no amount cannot be matched on
                its amount, which is the one thing that finds an entry keyed with an unusable reference.
              </p>
            )}
          </div>

          {cache.last_error && (
            <div className={`${T.meta} mx-3 mb-2 px-2 py-1.5 rounded border border-red-300 bg-red-50 text-red-800`}>
              Last sync error: {cache.last_error}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SyncCheck({ onFlash }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      let checked = 0, missing = 0; const drift = [];
      for (let pass = 0; pass < 40; pass++) {
        const r = await api.post('sage/sync-check', { limit: 25 });
        checked += r.checked; missing += r.missing; drift.push(...(r.drift || []));
        setProgress(`Checked ${checked}, ${r.remaining} to go…`);
        if (!r.remaining || !r.checked) break;
      }
      setProgress(null);
      setResult({ checked, missing, drift });
      onFlash(drift.length ? 'warn' : 'ok',
        drift.length ? `${drift.length} of ${checked} differ from Sage.` : `All ${checked} match Sage.`);
    } catch (e) { setProgress(null); onFlash('error', e.message || 'The check failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>Still in step with Sage?</span>
        <button onClick={run} disabled={busy} className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}>
          {busy ? (progress || 'Checking…') : 'Check posted invoices'}
        </button>
      </div>
      <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
        Reads each posted invoice back from Sage and compares the total and the date. Finds entries that were
        edited in Sage afterwards, and entries that have been deleted there. Nothing is changed either side.
      </p>

      {result && (
        <div className="px-3 pb-2">
          {result.missing > 0 && (
            <div className={`${T.body} px-2 py-1.5 rounded border border-red-300 bg-red-50 text-red-800 mb-1`}>
              {result.missing} invoice{result.missing === 1 ? '' : 's'} ops believes are in Sage {result.missing === 1 ? 'is' : 'are'} not there.
              The Sage id is kept rather than cleared, so nothing gets posted a second time by accident.
            </div>
          )}
          {result.drift.map(d => (
            <div key={d.id} className={`${T.meta} px-2 py-1 rounded border mb-1 ${
              d.kind === 'missing' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              <strong>{d.invoice_number || d.id}</strong> — {d.detail}
            </div>
          ))}
          {!result.drift.length && (
            <div className={`${T.body} text-emerald-800`}>All {result.checked} checked invoices match Sage.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Duplicate documents ─────────────────────────────────────────────────────
//
// Field matching works from what was READ off the invoice, and anything read off
// a document can be misread. The bytes cannot: two records carrying the same PDF
// are the same invoice however differently the extraction read them.
//
// The push-time check stops a duplicate reaching Sage. This finds the ones
// already sitting in ops, which is the more useful question when you are about
// to start posting a back catalogue.
function DuplicateDocuments({ onFlash }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api.get('sage/duplicate-documents')); }
    catch (e) { onFlash('error', e.message || 'Could not load that.'); }
  }, [onFlash]);
  useEffect(() => { load(); }, [load]);

  // Batched, because each invoice is a read from storage and a Worker has a time
  // limit. Loops until the server says nothing is left.
  const backfill = async () => {
    setBusy(true);
    try {
      let total = 0;
      for (let pass = 0; pass < 60; pass++) {
        const r = await api.post('sage/hash-documents', { limit: 100 });
        total += r.hashed;
        setProgress(`Hashed ${total}, ${r.remaining} to go…`);
        if (!r.remaining || (!r.hashed && !r.skipped)) break;
      }
      setProgress(null);
      onFlash('ok', `Hashed ${total} invoice documents.`);
      await load();
    } catch (e) { setProgress(null); onFlash('error', e.message || 'Hashing failed.'); }
    finally { setBusy(false); }
  };

  const groups = data?.groups || [];
  const alreadyDoubled = groups.filter(g => g.in_sage > 1).length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>The same document logged twice</span>
        <button onClick={backfill} disabled={busy} className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}>
          {busy ? (progress || 'Hashing…') : 'Hash older invoices'}
        </button>
      </div>
      <p className={`${T.meta} text-gray-500 px-3 py-1.5`}>
        Invoices arriving now are hashed automatically. Older ones need hashing once, and that is where the
        duplicates already are. Matched on the file itself, so it holds even when the number, the amount and the
        date were all misread.
      </p>

      {alreadyDoubled > 0 && (
        <p className={`${T.meta} text-red-800 bg-red-50 px-3 py-1.5 border-y border-red-200`}>
          {alreadyDoubled} document{alreadyDoubled === 1 ? ' has' : 's have'} been posted to Sage more than once.
          That is a duplicate already in the ledger, not a risk of one: it needs correcting in Sage.
        </p>
      )}

      <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
        {groups.map(g => (
          <div key={g.sha} className="px-3 py-1.5">
            <div className={`${T.meta} ${g.in_sage > 1 ? 'text-red-700 font-semibold' : 'text-gray-500'}`}>
              {g.rows.length} records share one document
              {g.in_sage > 1 ? ` — ${g.in_sage} of them are in Sage` : g.in_sage === 1 ? ' — one is in Sage' : ''}
            </div>
            {g.rows.map(r => (
              <div key={r.id} className={`${T.body} text-gray-700 flex items-center gap-2 flex-wrap`}>
                <span className="text-gray-800">{r.supplier || 'Unknown'}</span>
                <span className={`${T.meta} text-gray-400`}>
                  {r.invoice_number || 'no number'}{r.invoice_date ? ` · ${formatDate(r.invoice_date)}` : ''}
                </span>
                <span className={`${T.meta} tabular-nums text-gray-500`}>{money(r.total_incl_vat)}</span>
                {r.sage_invoice_id && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">in Sage</span>}
              </div>
            ))}
          </div>
        ))}
        {data && !groups.length && (
          <div className={`${T.body} text-gray-500 px-3 py-3`}>No invoice document appears twice.</div>
        )}
      </div>
    </div>
  );
}

// ── Milestones out to Sage ──────────────────────────────────────────────────
//
// The process this fits into, unchanged in shape:
//
//   milestone set to Ready to bill -> an admin views it -> creates it in Sage ->
//   puts the Sage invoice id back on the milestone -> status moves to Invoiced
//
// Only the third and fourth steps were manual. A person still moves the
// milestone and a person still looks at it before anything is sent.
//
// A sales invoice goes to a customer and declares output VAT we then owe HMRC,
// so this refuses far more readily than the purchase queue and asks for a
// confirmation naming the client and the amount.
function BillingQueue({ onFlash }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(null);
  const [tab, setTab]   = useState('ready');

  const load = useCallback(async () => {
    try { setData(await api.get('sage/billing-queue')); }
    catch (e) { onFlash('error', e.message || 'Could not load the billing queue.'); }
  }, [onFlash]);
  useEffect(() => { load(); }, [load]);

  const post = async (row) => {
    if (!window.confirm(
      `Raise a sales invoice in Sage?\n\n${row.client || 'Unknown client'}\n${row.reference} ${row.milestone_code}\n`
      + `${money(row.net)} + ${money(row.vat)} VAT = ${money(row.gross)}\n\n`
      + 'This invoices a customer and declares output VAT. The milestone moves to Invoiced.')) return;
    setBusy(row.id);
    try {
      const r = await api.post(`sage/milestone/${row.id}/post`, {});
      onFlash('ok', `Invoiced in Sage${r.sage_invoice_number ? ` as ${r.sage_invoice_number}` : ''}. The milestone is now Invoiced.`);
      await load();
    } catch (e) { onFlash('error', `${row.milestone_code}: ${e.message}`); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const rows = tab === 'ready' ? data.ready : data.blocked;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>Milestones to invoice ({data.ready_status})</span>
        <div className="flex gap-1">
          {[['ready', `Ready (${data.ready.length})`], ['blocked', `Needs attention (${data.blocked.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-2.5 py-1 rounded text-[10px] font-semibold border ${
                tab === k ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{l}</button>
          ))}
        </div>
      </div>

      {!data.posting_enabled && (
        <p className={`${T.meta} text-amber-800 bg-amber-50 px-3 py-1.5 border-b border-amber-200`}>
          Raising sales invoices is switched off, so the buttons below will refuse. It is a separate switch from
          purchase posting on purpose: invoicing customers is a bigger decision than recording what we have been billed.
        </p>
      )}
      {!data.default_ledger_account_id && (
        <p className={`${T.meta} text-amber-800 bg-amber-50 px-3 py-1.5 border-b border-amber-200`}>
          No default revenue account is set (<code>sage_sales_ledger_account_id</code>), so nothing can be raised.
          Blank means refuse rather than guess which revenue code to book income to.
        </p>
      )}

      <div className="max-h-96 overflow-y-auto overflow-x-auto">
        <table className="w-full">
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => (
              <React.Fragment key={r.id}>
                <tr className={r.ready ? '' : 'bg-amber-50/40'}>
                  <td className="px-3 py-1.5">
                    <div className={`${T.body} font-semibold text-gray-800`}>{r.client || 'Unknown client'}</div>
                    <div className={`${T.meta} text-gray-400`}>
                      {r.reference} {r.milestone_code}
                      {r.customer_po ? ` · PO ${r.customer_po}` : ' · no customer PO'}
                      {r.due_date ? ` · due ${formatDate(r.due_date)}` : ''}
                    </div>
                  </td>
                  <td className={`${T.body} px-3 py-1.5 text-right tabular-nums text-gray-600 whitespace-nowrap`}>
                    {money(r.net)} + {money(r.vat)}
                    {r.vat_percent != null && <span className={`${T.meta} text-gray-400`}> ({r.vat_percent}%)</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    {r.ready ? (
                      <button onClick={() => post(r)} disabled={busy === r.id}
                        className={`${btn} bg-navy text-white border-navy hover:bg-navy/90`}>
                        {busy === r.id ? 'Raising…' : 'Raise in Sage'}
                      </button>
                    ) : (
                      <button onClick={() => setOpen(o => o === r.id ? null : r.id)}
                        className={`${T.meta} text-navy hover:underline`}>
                        {open === r.id ? 'close' : `${r.problems.length} to fix`}
                      </button>
                    )}
                  </td>
                </tr>
                {open === r.id && (
                  <tr className="bg-amber-50/40">
                    <td colSpan={3} className="px-3 pb-2">
                      {r.problems.map((p, i) => <div key={i} className={`${T.meta} text-red-700`}>✕ {p}</div>)}
                      {(r.warnings || []).map((w, i) => <div key={`w${i}`} className={`${T.meta} text-amber-800`}>⚠ {w}</div>)}
                      {r.last_error && <div className={`${T.meta} text-red-700 mt-1`}>Last attempt: {r.last_error}</div>}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!rows.length && (
              <tr><td colSpan={3} className={`${T.body} text-gray-500 px-3 py-4 text-center`}>
                {tab === 'ready' ? `Nothing is marked "${data.ready_status}".` : 'Nothing is blocked.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(rows.length > 0 || data.blocked.length > 0) && (
        <p className={`${T.meta} text-gray-500 px-3 py-1.5 border-t border-gray-100`}>
          Raising one records the Sage invoice number back on the milestone and moves it to Invoiced — the two steps
          that were done by hand.
        </p>
      )}
    </div>
  );
}

// ── The post queue ──────────────────────────────────────────────────────────
//
// Posting used to be reachable only by opening the right email in the Accounts
// Inbox and finding the invoice inside it. That works for one invoice you
// already know about and is useless for working through a backlog, which is
// what "get everything into Sage" actually means.
//
// Readiness is decided by the same builder the push uses, so "ready" here means
// the push would genuinely succeed rather than merely look plausible. There is
// still no bulk post: each row is a separate, deliberate press.
function PostQueue({ onFlash }) {
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(null);   // invoice id being posted
  const [open, setOpen]   = useState(null);   // invoice id whose problems are shown
  const [tab, setTab]     = useState('ready');

  const load = useCallback(async () => {
    try { setData(await api.get('sage/ready-to-post')); }
    catch (e) { onFlash('error', e.message || 'Could not load the queue.'); }
  }, [onFlash]);
  useEffect(() => { load(); }, [load]);

  // Overruling the matcher. A reason is required and recorded against the
  // invoice: this is the one control a person can use to put a duplicate in the
  // ledger, so "someone overrode it at some point" is not good enough.
  const notDuplicate = async (row) => {
    const note = window.prompt(
      `Why is ${row.invoice_number || 'this invoice'} from ${row.supplier || 'this supplier'} not a duplicate?\n\n`
      + 'Check the amounts and dates against what is already in Sage first. This is recorded against the invoice.');
    if (note === null) return;
    try {
      await api.post(`sage/invoice/${row.id}/not-duplicate`, { note });
      onFlash('ok', 'Recorded. The invoice can now be posted.');
      await load();
    } catch (e) { onFlash('error', e.message || 'Could not record that.'); }
  };

  const post = async (row) => {
    // Same rule as the Accounts Inbox: a clean invoice posts without being
    // interrogated about it. Everything on this tab has already passed the
    // check, and asking "are you sure" about each of forty of them only teaches
    // people to click through without reading.
    //
    // One difference from the inbox, and it is why the confirmation here quotes
    // the figures: this is a list, so the risk is pressing the wrong ROW rather
    // than posting a wrong invoice. That risk is answered by the row naming its
    // supplier and amount on screen, not by a dialog.
    if (row.warnings?.length && !window.confirm(
      `Post ${row.invoice_number || 'this invoice'} from ${row.supplier || 'unknown supplier'} to Sage?\n\n`
      + `Net ${money(row.net)}, VAT ${money(row.vat)}.\n\n`
      + row.warnings.map(w => `• ${w}`).join('\n')
    )) return;
    setBusy(row.id);
    try {
      const r = await api.post(`sage/invoice/${row.id}/push`, {});
      onFlash(r.attachment && !r.attachment.ok ? 'warn' : 'ok',
        `Posted ${row.invoice_number || 'invoice'} to Sage.`
        + (r.attachment?.ok ? ' PDF attached.' : r.attachment ? ` The PDF did not attach: ${r.attachment.error}` : ''));
      await load();
    } catch (e) { onFlash('error', `${row.invoice_number || 'Invoice'}: ${e.message}`); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const rows = tab === 'ready' ? data.ready : data.blocked;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
        <span className={`${T.label} text-gray-500`}>Invoices not yet in Sage</span>
        <div className="flex gap-1">
          {[['ready', `Ready (${data.ready.length})`], ['blocked', `Needs attention (${data.blocked.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-2.5 py-1 rounded text-[10px] font-semibold border ${
                tab === k ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{l}</button>
          ))}
        </div>
      </div>

      {!data.posting_enabled && (
        <p className={`${T.meta} text-amber-800 bg-amber-50 px-3 py-1.5 border-b border-amber-200`}>
          Posting is switched off, so the buttons below will refuse. Tick "Allow posting to Sage" above once a
          preview has been checked against a real invoice.
        </p>
      )}

      <div className="max-h-96 overflow-y-auto overflow-x-auto">
        <table className="w-full">
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => (
              <React.Fragment key={r.id}>
                <tr className={r.ready ? '' : 'bg-amber-50/40'}>
                  <td className={`${T.body} px-3 py-1.5`}>
                    <div className="font-semibold text-gray-800">{r.supplier || 'Unknown supplier'}</div>
                    <div className={`${T.meta} text-gray-400`}>
                      {r.invoice_number || 'no number'}
                      {r.invoice_date ? ` · ${formatDate(r.invoice_date)}` : ''}
                      {r.has_document ? '' : ' · no document stored'}
                    </div>
                  </td>
                  <td className={`${T.body} px-3 py-1.5 text-right tabular-nums text-gray-600 whitespace-nowrap`}>
                    {money(r.net)} + {money(r.vat)}
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    {r.ready ? (
                      <button onClick={() => post(r)} disabled={busy === r.id}
                        className={`${btn} bg-navy text-white border-navy hover:bg-navy/90`}>
                        {busy === r.id ? 'Posting…' : 'Post'}
                      </button>
                    ) : (
                      <button onClick={() => setOpen(o => o === r.id ? null : r.id)}
                        className={`${T.meta} text-navy hover:underline`}>
                        {open === r.id ? 'close' : `${r.problems.length} to fix`}
                      </button>
                    )}
                  </td>
                </tr>
                {open === r.id && (
                  <tr className="bg-amber-50/40">
                    <td colSpan={3} className="px-3 pb-2">
                      {r.problems.map((p, i) => (
                        <div key={i} className={`${T.meta} text-red-700`}>✕ {p}</div>
                      ))}
                      {(r.warnings || []).map((w, i) => (
                        <div key={`w${i}`} className={`${T.meta} text-amber-800`}>⚠ {w}</div>
                      ))}
                      {r.last_error && <div className={`${T.meta} text-red-700 mt-1`}>Last attempt: {r.last_error}</div>}
                      {/* The way past a duplicate judgement. It exists because
                          the matcher can be wrong in a way that matters:
                          suppliers reuse invoice numbers, and an invoice that
                          can never be posted is one somebody keys into Sage by
                          hand instead. It clears duplicate judgements only. */}
                      {r.overridable && (
                        <button onClick={() => notDuplicate(r)}
                          className={`${btn} mt-1.5 border-amber-400 text-amber-800 bg-white hover:bg-amber-50`}>
                          This is not a duplicate
                        </button>
                      )}
                      <div className={`${T.meta} text-gray-500 mt-1`}>Fix these on the invoice in the Accounts Inbox, then refresh.</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!rows.length && (
              <tr><td colSpan={3} className={`${T.body} text-gray-500 px-3 py-4 text-center`}>
                {tab === 'ready' ? 'Nothing is ready to post.' : 'Nothing is blocked.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Recent activity ─────────────────────────────────────────────────────────
// sage_push_log has always recorded the exact payload sent and what came back,
// but nothing could read it. During the first real posts that history is the
// difference between "Sage said no" and knowing which field it objected to.
function Activity() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => { if (show && !rows) api.get('sage/activity').then(setRows).catch(() => setRows([])); }, [show, rows]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button onClick={() => setShow(v => !v)}
        className="w-full px-3 py-2 border-b border-gray-200 flex items-center justify-between text-left">
        <span className={`${T.label} text-gray-500`}>Recent Sage activity</span>
        <span className={`${T.meta} text-gray-400`}>{show ? 'hide' : 'show'}</span>
      </button>
      {show && (
        <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
          {(rows || []).map(r => (
            <div key={r.id} className="px-3 py-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                  r.status === 'ok' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{r.action}</span>
                <span className={`${T.body} text-gray-800`}>{r.invoice_number || r.invoice_id || '—'}</span>
                <span className={`${T.meta} text-gray-400`}>{r.supplier_name}</span>
                <span className={`${T.meta} text-gray-400 ml-auto`}>{r.created_at}</span>
                <button onClick={() => setOpen(o => o === r.id ? null : r.id)} className={`${T.meta} text-navy hover:underline`}>
                  {open === r.id ? 'hide' : 'detail'}
                </button>
              </div>
              {r.error && <div className={`${T.meta} text-red-700 mt-0.5`}>{r.error}</div>}
              {open === r.id && (
                <pre className="mt-1 bg-gray-50 border border-gray-200 rounded p-2 text-[9px] overflow-x-auto max-h-52">
{`SENT:\n${r.request_json || '(none)'}\n\nRECEIVED:\n${r.response_json || '(none)'}`}
                </pre>
              )}
            </div>
          ))}
          {rows && !rows.length && <div className={`${T.meta} text-gray-500 px-3 py-3`}>Nothing yet.</div>}
          {!rows && <div className={`${T.meta} text-gray-400 px-3 py-3`}>Loading…</div>}
        </div>
      )}
    </div>
  );
}

export default function SagePage() {
  const [status, setStatus] = useState(null);
  const [ref, setRef] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [probes, setProbes] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [showDefaults, setShowDefaults] = useState(false);

  const load = useCallback(async () => {
    const [st, rf] = await Promise.all([
      api.get('sage/status').catch(() => null),
      api.get('sage/reference').catch(() => null),
    ]);
    setStatus(st); setRef(rf);
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (kind, text) => { setMsg({ kind, text }); setTimeout(() => setMsg(null), 10000); };

  const connect = async () => {
    try {
      const { url } = await api.get('sage/connect');
      // Full navigation, not a popup: Sage's consent screen refuses to frame
      // and the callback needs to land back on this origin with the session.
      window.location.href = url;
    } catch (e) { flash('error', e.message || 'Could not start the Sage connection'); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post('sage/sync', {});
      // Kept on screen rather than only flashed. The sync reports per-record
      // problems, and a flash that fades is exactly where "every tax rate
      // failed to save" went unnoticed.
      setLastSync(r);
      const nothing = !r.ledger_accounts && !r.tax_rates && !r.contacts;
      flash(r.errors?.length || nothing ? 'warn' : 'ok',
        `Pulled ${r.ledger_accounts} ledger accounts, ${r.tax_rates} tax rates, ${r.contacts} suppliers.`
        // Only mentioned when there are some. On a business with no EU suppliers
        // Sage does not offer them, and "0 EU Goods/Services types" reads as a
        // fault rather than as nothing to fetch.
        + (r.eu_goods_services_types ? ` ${r.eu_goods_services_types} EU Goods/Services types.` : '')
        + (r.eu_goods_services_note ? ` ${r.eu_goods_services_note}` : '')
        + (r.errors?.length ? ` Problems: ${r.errors.join('; ')}` : '')
        + (nothing && !r.errors?.length ? ' Sage answered without an error but sent nothing back — press Diagnose.' : ''));
      await load();
    } catch (e) { flash('error', e.message || 'Sync failed'); }
    finally { setBusy(false); }
  };

  const diagnose = async () => {
    setBusy(true); setProbes(null);
    try {
      // The plain list probes confirm the connection. The two tax rate probes
      // after them are the ones that answer a live question: the plain list
      // came back with names and no percentages, so these show whether asking
      // for the full representation, or fetching one rate on its own, gets the
      // number Sage will not put in a summary.
      const paths = [
        '/businesses', '/ledger_accounts', '/tax_rates', '/contacts',
        '/tax_rates?attributes=all',
        '/tax_rates/GB_STANDARD',
      ];
      setProbes(await Promise.all(paths.map(p => api.get('sage/probe', { path: p }).catch(e => ({
        path: p, ok: false, error: e.message || 'request failed',
      })))));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect from Sage? Nothing already posted is affected.')) return;
    setBusy(true);
    await api.post('sage/disconnect', {}).catch(() => {});
    setBusy(false);
    await load();
  };

  // Turning posting on is the most consequential switch in the app, so it asks
  // once and is audited server-side with its old value.
  const onSetting = async (key, value) => {
    if (key === 'sage_posting_enabled' && value === true
        && !window.confirm('Allow invoices to be posted to Sage?\n\nEach one still needs a person to press Post on a specific invoice, and an invoice already in Sage can never be posted twice.')) {
      return;
    }
    try {
      await api.put('sage/settings', { [key]: value });
      await load();
    } catch (e) { flash('error', e.message || 'Could not change that setting.'); }
  };

  const mapCategory = async (id, patch) => {
    await api.put(`sage/map-category/${id}`, patch).catch(() => flash('error', 'Could not save that default'));
    await load();
  };
  // Which route a supplier's invoices take while both systems are running.
  const setSupplierRoute = async (supplier_id, route) => {
    try {
      await api.put('sage/route', { supplier_id, route: route || null });
      await load();
    } catch (e) { flash('error', e.message || 'Could not change that route.'); }
  };

  const mapSupplier = async (id, sage_contact_id) => {
    if (id === undefined) return load();   // called with no arguments to refresh
    await api.put(`sage/map-supplier/${id}`, { sage_contact_id: sage_contact_id || null })
      .catch(() => flash('error', 'Could not save that link'));
    await load();
  };

  // The supplier's usual nominal code. Sent as null when cleared, so "no usual
  // code" is stored as the absence of one rather than as an empty id that
  // matches no ledger account.
  const setSupplierLedger = async (id, ledgerId) => {
    try {
      await api.put(`sage/map-supplier/${id}`, { default_sage_ledger_account_id: ledgerId || null });
      flash('ok', ledgerId ? 'Saved. New invoices from this supplier will arrive with that code.' : 'Usual code cleared.');
    } catch (e) {
      flash('error', e.message || 'Could not save that code.');
    }
    await load();
  };

  const ledgerLabel = (l) => `${l.nominal_code ? `${l.nominal_code} — ` : ''}${l.display_name || l.name}`;

  return (
    <div className="fade-in">
      <div className="mb-4">
        <h2 className={`${T.page} text-navy`}>Sage</h2>
        <p className={`${T.meta} text-gray-500 mt-0.5`}>
          Connection, reference data, supplier links, and the queue of invoices waiting to go to Sage.
        </p>
      </div>

      {msg && (
        <div className={`mb-3 rounded-lg border p-3 ${T.body} ${
          msg.kind === 'ok' ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
            : msg.kind === 'warn' ? 'bg-amber-50 border-amber-300 text-amber-900'
            : 'bg-red-50 border-red-300 text-red-900'}`}>{msg.text}</div>
      )}

      <div className="space-y-3">
        <StatusPanel status={status} onConnect={connect} onSync={sync} onDisconnect={disconnect}
          onDiagnose={diagnose} busy={busy} />

        {/* What the last sync could not do. On screen until it is dismissed,
            because these are per-record failures and the one that mattered
            ("every tax rate insert failed") is invisible in a fading flash. */}
        {lastSync?.errors?.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className={`${T.strong} text-amber-900`}>
                The last sync reported {lastSync.errors.length} {lastSync.errors.length === 1 ? 'problem' : 'problems'}
              </div>
              <button onClick={() => setLastSync(null)} className={`${T.meta} text-amber-700 hover:text-amber-900`}>close</button>
            </div>
            <ul className={`${T.meta} text-amber-900 mt-1 space-y-0.5 list-disc pl-4`}>
              {lastSync.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
            {lastSync.errors.length > 20 && (
              <div className={`${T.meta} text-amber-800 mt-1`}>…and {lastSync.errors.length - 20} more.</div>
            )}
          </div>
        )}

        {probes && <Diagnostics probes={probes} onClose={() => setProbes(null)} />}

        {status?.connected && <TaxRates rates={ref?.tax_rates} />}

        {status?.connected && (
          <div className={`rounded-lg border p-3 ${status.posting_enabled ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
            <div className={`${T.strong} ${status.posting_enabled ? 'text-amber-900' : 'text-gray-800'}`}>
              {status.posting_enabled ? 'Posting to Sage is ON' : 'Preview only — nothing is being posted'}
            </div>
            <p className={`${T.meta} ${status.posting_enabled ? 'text-amber-800' : 'text-gray-600'} mt-1`}>
              {status.posting_enabled
                ? 'Invoices can be posted from the queue below or from the Accounts Inbox. Each one still needs a person to press the button, and an invoice already in Sage can never be posted twice.'
                : 'The queue below shows what would be sent and what is blocking each one. When it looks right, tick the box.'}
            </p>
            <div className="mt-2 pt-2 border-t border-gray-200/70 space-y-1.5">
              {[
                ['sage_posting_enabled', status.posting_enabled, 'Allow posting to Sage',
                 'Off means the checks run but nothing is ever sent. Turn this on only once a preview has been read against a real invoice.'],
                ['sage_require_vat_evidence', status.require_vat_evidence, 'Require VAT evidence before posting',
                 'Refuses to post an invoice claiming VAT without the supplier VAT number and a stored copy of the document. Off puts the reclaim at risk if HMRC ask.'],
                ['autoentry_forward_enabled', status.autoentry_forward_enabled, 'Still forward invoices to AutoEntry',
                 'Off while Sage posting is being tested. An invoice going both ways is a duplicate in the ledger.'],
                ['sage_sales_posting_enabled', status.sales_posting_enabled, 'Allow milestones to be invoiced to customers',
                 'Separate from purchase posting on purpose. A wrong purchase invoice overstates cost and can be corrected quietly; a wrong sales invoice goes to a customer and declares VAT we then owe.'],
              ].map(([key, value, label, help]) => (
                <label key={key} className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!value} className="mt-0.5"
                    onChange={e => onSetting(key, e.target.checked)} />
                  <span>
                    <span className={`${T.body} font-semibold text-gray-800`}>{label}</span>
                    <span className={`${T.meta} text-gray-500 block`}>{help}</span>
                  </span>
                </label>
              ))}

              {/* No longer a switch. The document is the evidence behind the
                  VAT reclaim, so it goes with every posting; leaving it
                  optional only meant it could be off without anyone realising. */}
              <p className={`${T.meta} text-gray-500`}>
                The supplier PDF is attached to the Sage entry on every posting. If an upload fails, the invoice is
                retried automatically overnight and shows here until it succeeds.
              </p>

              <div className="flex items-center gap-2 flex-wrap pt-1">
                <span className={`${T.body} font-semibold text-gray-800`}>New invoices go to</span>
                <select className={sel} value={status.default_route || 'autoentry'}
                  onChange={e => onSetting('sage_default_route', e.target.value)}>
                  <option value="autoentry">AutoEntry, unless the supplier is moved over</option>
                  <option value="ops">Sage, posted from here</option>
                </select>
              </div>

              {/* The combination that leaves an invoice with nowhere to go. Both
                  settings are individually sensible and together they deadlock,
                  so it is called out rather than discovered one refused invoice
                  at a time. */}
              {!status.autoentry_forward_enabled && status.default_route !== 'ops' && (
                <div className="mt-1 px-2 py-1.5 rounded border border-red-300 bg-red-50">
                  <div className={`${T.strong} text-red-900`}>Invoices currently have nowhere to go</div>
                  <p className={`${T.meta} text-red-800 mt-0.5`}>
                    Forwarding to AutoEntry is off, but new invoices still default to the AutoEntry route, so posting
                    from here is refused too. Either switch the default above to Sage, or move individual suppliers
                    across on the supplier list.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {status?.connected && <PostQueue onFlash={flash} />}

        {status?.connected && <BillingQueue onFlash={flash} />}

        {status?.connected && <Reconciliation onFlash={flash} />}

        {status?.connected && <DuplicateDocuments onFlash={flash} />}

        {status?.connected && ref && (
          <SupplierMapping data={ref} onMap={mapSupplier} onRoute={setSupplierRoute}
            onLedger={setSupplierLedger}
            defaultRoute={ref?.default_route || 'autoentry'} onFlash={flash} />
        )}

        {status?.connected && <LedgerCache onFlash={flash} />}

        {status?.connected && <SageCutover onFlash={flash} />}

        {status?.connected && <SyncCheck onFlash={flash} />}

        {status?.connected && <Activity />}

        {/* Category defaults. Collapsed, because this is no longer where the
            posting decision is made — it only pre-fills the picker on the
            invoice, and an unmapped category blocks nothing. */}
        {status?.connected && ref && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button onClick={() => setShowDefaults(v => !v)}
              className="w-full px-3 py-2 border-b border-gray-200 flex items-center justify-between text-left">
              <span className={`${T.label} text-gray-500`}>Suggested ledger account per category (optional)</span>
              <span className={`${T.meta} text-gray-400`}>{showDefaults ? 'hide' : 'show'}</span>
            </button>
            {showDefaults && (
              <>
                <p className={`${T.meta} text-gray-500 px-3 py-2`}>
                  These only pre-fill the ledger picker when an invoice is coded. The person entering the invoice
                  can always choose a different code, which is the point: one "Vehicles" category covers fuel,
                  servicing and vehicle insurance, and those are three different nominal codes.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>{['Ops category', 'Suggested Sage ledger account'].map(h => (
                        <th key={h} className={`${T.label} text-gray-500 px-3 py-2 text-left`}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(ref.categories || []).map(c => (
                        <tr key={c.id}>
                          <td className={`${T.body} font-semibold text-gray-800 px-3 py-2`}>{c.name}</td>
                          <td className="px-3 py-2">
                            <SearchPicker
                              options={(ref.ledger_accounts || []).map(l => ({ value: l.id, label: ledgerLabel(l) }))}
                              value={c.sage_ledger_account_id || ''}
                              onChange={v => mapCategory(c.id, { sage_ledger_account_id: v || null })}
                              className={`${sel} w-full`} placeholder="Search nominal code or name…" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className={`${T.meta} text-gray-500 px-3 py-2 border-t border-gray-100`}>
                  There is no tax rate here on purpose. The VAT rate is worked out from each invoice's own net and
                  VAT figures and matched to the Sage rate that reproduces it, so a zero-rated invoice in a
                  standard-rated category posts correctly instead of being blocked.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
